<template>
    <v-dialog :value="value" persistent :transition="false" @keydown="onKeydown">
        <v-card height="var(--dialog-height)">
            <v-stepper v-model="step" elevation="0" class="stepper-container">
                <v-stepper-header class="stepper-header">
                    <v-stepper-step :step="1" editable>
                        {{ $t('choose_titleimg') }}
                    </v-stepper-step>
                    <v-stepper-step :step="2">
                        {{ $t('crop_imgs') }}
                    </v-stepper-step>
                </v-stepper-header>
                <v-stepper-items class="stepper-items">
                    <v-stepper-content :step="1" class="stepper-content">
                        <MainView
                            elevation="0"
                            session-key="mm-img-select-dialog"
                            :initial-selected="selectedImage"
                            :organizer-id="organizerId"
                            hide-multiselect
                            @select-image="selectedImage = $event"
                        />
                        <v-card-actions class="elevation-0 stepper-footer">
                            <v-spacer />
                            <PlainBtn :transparent="false" @click="$emit('input', false)">{{
                                $t('schliessen')
                            }}</PlainBtn>
                            <span v-if="selectedImage && selectedImage.animated" class="primary--text mr-1">
                                {{ $t('animated_img') }}
                            </span>
                            <ActionBtn :disabled="!selectedImage" @click="onImgChooseBtn">{{
                                $t('choose_next')
                            }}</ActionBtn>
                        </v-card-actions>
                    </v-stepper-content>

                    <v-stepper-content :step="2" class="stepper-content">
                        <!-- we show the image format tabs only after the step is active: this prevents a rendering issue with Cropperjs -->
                        <ImageFormatTabs
                            v-if="step === 2 && selectedImage"
                            class="image-format-tabs"
                            :image-formats-config="imageFormatsConfig"
                            :image-crop-data="finalCropData"
                            :image="selectedImage"
                            @crop="onCropInfo"
                        />
                        <v-card-actions class="crop-action-toolbar">
                            <v-spacer />
                            <plain-btn @click="$emit('input', false)">{{ $t('schliessen') }}</plain-btn>
                            <plain-btn @click="step = 1">{{ $t('back_sel') }}</plain-btn>
                            <ActionBtn :loading="loading" @click="onCropImageBtn">{{ $t('crop_imgs') }}</ActionBtn>
                        </v-card-actions>
                    </v-stepper-content>
                </v-stepper-items>
            </v-stepper>
        </v-card>
    </v-dialog>
</template>

<script>
import MainView from '@/components/media_manager/MainView.vue';
import ImageFormatTabs from '@/components/media_manager/ImageFormatTabs.vue';
import { Image } from '@/models/Image';

/**
 * This dialog shows a 2-step wizard:
 * 1) lets the user select an image from the Media manager
 * 2) shows the Image Format Crop wizard, to define the image crop
 *    per required format.
 *
 * It emits the following events:
 * - input(false): If clicked on cancel, indicate the dialog close (to be used in v-model with value/input)
 * - image-crop(cropData, image): Emits the crop data and the selected image, if the user clicks on "crop"
 * - image-select(image): Emits the selected image only: this is the case when an image is not cropped, e.g it is an animated image
 */

export default {
    name: 'MediaManagerSelectFormatDialog',
    components: {
        MainView,
        ImageFormatTabs,
    },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        /**
         * Image format configs for a specific scope (e.g. magazin), structured like GLOBALS.featured_images_config.magazin, e.g.:
         * {
         *     front_header: {
         *         ident: 'front_header',
         *         width: 400,
         *         height: 300,
         *         overlay_preview: true,
         *         ....
         *     }
         * }
         */
        imageFormatsConfig: {
            type: Object,
            required: true,
        },

        // initial / crop info to use when opening / changing an existing image
        imageCropData: {
            type: Object,
            default: null,
            required: false,
        },

        // already selected image: In this case we step directly to the image crop dialog
        initialImage: {
            type: Image,
            default: null,
            required: false,
        },

        initialStep: {
            type: Number,
            default: 1,
            required: false,
        },
        organizerId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            step: this.initialImage && !this.initialImage.animated ? this.initialStep : 1,
            selectedImage: this.initialImage || null,
            finalCropData: typeof this.imageCropData === 'object' ? { ...this.imageCropData } : {},
        };
    },
    methods: {
        onImgChooseBtn() {
            if (this.selectedImage) {
                if (this.selectedImage.animated) {
                    this.$emit('image-select', this.selectedImage);
                } else {
                    this.finalCropData = {};
                    this.step = 2;
                }
            }
        },

        /**
         * Stores the crop info of a single crop change for a specific format,
         * issued by the ImageFormatTabs component
         */
        onCropInfo(confKey, cropData, image) {
            this.finalCropData[confKey] = cropData;
            if (image) {
                this.selectedImage = image;
            }
        },

        onCropImageBtn() {
            if (this.checkCropData()) {
                this.$emit('image-crop', { ...this.finalCropData }, this.selectedImage);
            } else {
                alert('Es sind noch nicht alle Bildformate definiert. Bitte definieren Sie alle Formate.');
            }
        },

        /**
         * Checks if all crop data is defined: for each key in imageFormatsConfig,
         * there must be a corresponding object in finalCropData.
         */
        checkCropData() {
            for (let key in this.imageFormatsConfig) {
                if (!this.finalCropData[key]) {
                    return false;
                }
            }
            return true;
        },

        onKeydown(e) {
            // esc: close
            if (e.keyCode === 27) {
                this.$emit('input', false);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.stepper-container {
    height: 100%;

    display: flex;
    flex-direction: column;

    .stepper-header {
        justify-content: flex-start;
    }

    .stepper-items {
        flex: 1;

        .stepper-content {
            height: 100%;
            padding: 0;

            :deep(.v-stepper__wrapper) {
                height: 100%;

                display: flex;
                flex-direction: column;

                .stepper-footer {
                    position: absolute;
                    bottom: 0;
                    right: 0;

                    background-color: white;
                    padding: 5px;
                    border-top-left-radius: 5px;
                    border-top: 1px solid var(--gray-border-color);
                    border-left: 1px solid var(--gray-border-color);
                }

                .image-format-tabs {
                    height: calc(100% - 50px);
                }

                .crop-action-toolbar {
                    height: 50px;
                }
            }
        }
    }
}
</style>
